import React from 'react'
import {
	Container,
	Flex,
	Grid,
	Link as ChakraLink,
	Text,
	Heading,
	Icon,
} from '@chakra-ui/react'
import Link from 'next/link'
import { FaFacebook, FaInstagram, FaLinkedin, FaYoutube } from 'react-icons/fa6'
import posthog from 'posthog-js'
import tenant from '@/lib/tenant'
import tenantData from '@crosure/tenant/data'
import type { SiteNavigationData } from '@/lib/siteNavigation'

/** `siteNavigation` is the Studio-merged nav config from
 *  `loadSiteNavigation()` (getServerSideProps → props). When a caller hasn't
 *  been updated to pass it yet, this falls back to the bundle-only reads
 *  Footer always used — zero regression for un-migrated pages. */
export const Footer = ({
	siteNavigation,
}: {
	siteNavigation?: SiteNavigationData
}) => {
	const bundleNavigation = (tenantData as any)?.landing?.navigation ?? {
		primary: { items: [] },
		secondary: { items: [] },
	}
	const navigation = siteNavigation
		? {
				primary: { items: siteNavigation.navPrimary },
				secondary: { items: siteNavigation.navSecondary },
			}
		: bundleNavigation
	const socialIcons = {
		Instagram: FaInstagram,
		LinkedIn: FaLinkedin,
		YouTube: FaYoutube,
		Facebook: FaFacebook,
	}
	const socialLinks = (
		siteNavigation?.socialLinks ??
		tenant.brand.socialLinks ??
		[]
	).map(link => ({
		...link,
		icon: socialIcons[link.label as keyof typeof socialIcons] ?? FaLinkedin,
	}))
	const footerHeadline =
		siteNavigation?.footerHeadline || tenant.copy.footerHeadline
	const footerLegalText =
		siteNavigation?.footerLegalText || tenant.brand.footerLegalText
	const registeredAddress =
		siteNavigation?.registeredAddress || tenant.brand.registeredAddress

	const handlePosthogClick = ({
		posthogEvent,
	}: {
		posthogEvent: string | undefined
	}) => {
		if (posthogEvent) {
			posthog.capture(posthogEvent)
		}
	}

	const handleSocialClick = (label: string) => {
		posthog.capture(`social_media_button`, { param: label })
	}

	return (
		<>
			<Grid bg='primary.800' color='white'>
				<Container maxW='container.xl' py={{ base: 8, md: 16 }}>
					<Heading
						as='h2'
						fontSize={{ base: '2xl', md: '3xl', lg: '4xl', xl: '5xl' }}
						color='white'
						sx={{ textWrap: 'balance' }}
						maxW='16em'
						mb={12}
					>
						{footerHeadline}
					</Heading>
					<Flex mb={8}>
						<Grid
							gridColumn={{ base: 'span 6', md: 'span 2' }}
							rowGap={2}
							columnGap={16}
							gridTemplateColumns={{ sm: 'auto auto' }}
						>
							{navigation.primary.items.map(link => (
								<ChakraLink
									key={link.href}
									as={Link}
									href={link.href}
									color='white'
									_hover={{ color: 'white', textDecoration: 'underline' }}
									onClick={() =>
										handlePosthogClick({
											posthogEvent: link.posthogEventFooter,
										})
									}
									fontSize='lg'
									fontWeight='semibold'
								>
									{link.label}
								</ChakraLink>
							))}
						</Grid>
					</Flex>
					<Flex
						fontSize='sm'
						justifyContent='space-between'
						flexDir={{ base: 'column', md: 'row' }}
						gap={4}
						py={4}
					>
						<Flex gap={2} flexWrap='wrap'>
							<Text fontWeight='semibold'>{tenant.brand.appTitle}</Text>
							{registeredAddress && <Text>{registeredAddress}</Text>}
						</Flex>
						<Flex gap={6}>
							{socialLinks.map(link => (
								<ChakraLink
									key={link.label}
									href={link.href}
									aria-label={link.label}
									onClick={() => handleSocialClick(link.label)}
									isExternal
									color='white'
									target='_blank'
								>
									<Icon
										as={link.icon}
										width={6}
										height={6}
										strokeWidth={0}
										_hover={{ color: 'primary.100' }}
									/>
								</ChakraLink>
							))}
						</Flex>
					</Flex>
					<Flex
						fontSize='xs'
						borderTop='1px solid'
						borderColor='whiteAlpha.300'
						justifyContent='space-between'
						py={4}
						flexDir={{ base: 'column', md: 'row' }}
						gap={4}
					>
						<Flex gap={4} flexDir={{ base: 'column', md: 'row' }}>
							{navigation.secondary.items.map(link => (
								<ChakraLink
									key={link.href}
									as={Link}
									href={link.href}
									color='white'
									_hover={{ color: 'white', textDecoration: 'underline' }}
								>
									{link.label}
								</ChakraLink>
							))}
						</Flex>
						<Text opacity={0.75}>
							&copy; {new Date().getFullYear()} {tenant.brand.appTitle}. All
							rights reserved.
						</Text>
					</Flex>
					{footerLegalText && (
						<Text fontSize='xs' opacity={0.75} py={4}>
							{footerLegalText}
						</Text>
					)}
				</Container>
			</Grid>
		</>
	)
}
